/**
 * 浏览器本地存储探测 / 必要存储同意 / Privy Storage adapter。
 * 供登录闸门与 auth-token / Privy 共用；无 Vue 依赖。
 *
 * 中长期：会话优先 memory → localStorage（主路径）→ Cookie 兜底；后端 HttpOnly 就绪后再接 withCredentials。
 * 探测失败不再视为「不可登录」，仅标记 ephemeral（关页需重登）。
 */

export const STORAGE_BLOCKED_CODE = 'STORAGE_BLOCKED';

/** 用户已接受「必要本地存储」标记（与会话 token 同属必要用途） */
export const ESSENTIAL_STORAGE_CONSENT_KEY = 'ds-essential-storage-consent';

/** 本会话拒绝底部授权条（sessionStorage；写失败则仅内存） */
export const ESSENTIAL_STORAGE_DISMISS_KEY = 'ds-essential-storage-dismiss';

const PROBE_KEY = '__ds_storage_probe__';

/** 内存兜底：无痕 / 禁写时仍能记一次同意（本页会话有效） */
let memoryEssentialConsent = false;
/** 本会话已点「拒绝」 */
let memoryEssentialDismiss = false;

/** Privy adapter 内存层（localStorage 不可写时仍可完成登录手势） */
const privyMemory = new Map();

/**
 * @param {unknown} [cause]
 * @param {string} [reason]
 */
export function createStorageBlockedError(cause, reason = 'blocked') {
  const err = new Error('Unable to access storage');
  err.code = STORAGE_BLOCKED_CODE;
  err.storageReason = reason;
  if (cause != null) err.cause = cause;
  return err;
}

/**
 * @param {unknown} err
 * @returns {'blocked'|'quota'|'unknown'}
 */
export function classifyStorageError(err) {
  if (!err) return 'unknown';
  if (err?.code === STORAGE_BLOCKED_CODE) {
    return err.storageReason === 'quota' ? 'quota' : 'blocked';
  }
  const name = String(err?.name ?? '');
  const raw = String(err?.message ?? err?.error ?? '').toLowerCase();
  if (name === 'QuotaExceededError' || raw.includes('quota')) return 'quota';
  if (
    name === 'SecurityError' ||
    raw.includes('unable to access storage') ||
    raw.includes('failed to access storage') ||
    raw.includes('storage access denied') ||
    raw.includes('access is denied') ||
    raw.includes('the operation is insecure') ||
    raw.includes('storage is not available')
  ) {
    return 'blocked';
  }
  return 'unknown';
}

/** 是否为本地存储不可用类错误（Privy / localStorage / 我们抛出的码） */
export function isStorageBlockedError(err) {
  if (!err) return false;
  if (err.code === STORAGE_BLOCKED_CODE) return true;
  const name = String(err?.name ?? '');
  const raw = String(err?.message ?? err?.error ?? '').toLowerCase();
  if (raw.includes('unable to access storage')) return true;
  if (raw.includes('failed to access storage')) return true;
  if (raw.includes('storage access denied')) return true;
  if (raw.includes('storage is not available')) return true;
  if (name === 'SecurityError') {
    return (
      raw.includes('storage') ||
      raw.includes('localstorage') ||
      raw.includes('sessionstorage') ||
      raw.includes('access is denied') ||
      raw.includes('the operation is insecure')
    );
  }
  if (name === 'QuotaExceededError') return true;
  return false;
}

/**
 * @returns {{ ok: true } | { ok: false, reason: 'blocked'|'quota'|'unavailable' }}
 */
export function probeLocalStorage() {
  if (typeof localStorage === 'undefined') {
    return { ok: false, reason: 'unavailable' };
  }
  try {
    localStorage.setItem(PROBE_KEY, '1');
    localStorage.removeItem(PROBE_KEY);
    return { ok: true };
  } catch (err) {
    const kind = classifyStorageError(err);
    return { ok: false, reason: kind === 'quota' ? 'quota' : 'blocked' };
  }
}

/**
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export function probeSessionStorage() {
  if (typeof sessionStorage === 'undefined') {
    return { ok: false, reason: 'unavailable' };
  }
  try {
    sessionStorage.setItem(PROBE_KEY, '1');
    sessionStorage.removeItem(PROBE_KEY);
    return { ok: true };
  } catch (err) {
    const kind = classifyStorageError(err);
    return { ok: false, reason: kind === 'quota' ? 'quota' : 'blocked' };
  }
}

/**
 * 在用户手势下尝试解锁存储（第三方嵌入场景）；顶层站点多数直接返回探测结果。
 * @returns {Promise<{ ok: true } | { ok: false, reason: string }>}
 */
export async function ensureStorageAccess() {
  const first = probeLocalStorage();
  if (first.ok) return first;

  if (typeof document !== 'undefined' && typeof document.requestStorageAccess === 'function') {
    try {
      await document.requestStorageAccess();
    } catch {
      /* 拒绝或非第三方上下文 */
    }
    const second = probeLocalStorage();
    if (second.ok) return second;
  }

  return first;
}

export function hasEssentialStorageConsent() {
  if (memoryEssentialConsent) return true;
  try {
    if (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(ESSENTIAL_STORAGE_CONSENT_KEY) === '1'
    ) {
      memoryEssentialConsent = true;
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/** 标记用户已接受必要本地存储（写失败时仅记内存） */
export function markEssentialStorageConsent() {
  memoryEssentialConsent = true;
  memoryEssentialDismiss = false;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(ESSENTIAL_STORAGE_CONSENT_KEY, '1');
    }
  } catch {
    /* memory-only */
  }
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(ESSENTIAL_STORAGE_DISMISS_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function hasEssentialStorageDismiss() {
  if (memoryEssentialDismiss) return true;
  try {
    if (
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(ESSENTIAL_STORAGE_DISMISS_KEY) === '1'
    ) {
      memoryEssentialDismiss = true;
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/** 本会话拒绝底部授权条 */
export function markEssentialStorageDismiss() {
  memoryEssentialDismiss = true;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(ESSENTIAL_STORAGE_DISMISS_KEY, '1');
    }
  } catch {
    /* memory-only */
  }
}

/**
 * 登录前检查。localStorage 不可写时仍允许登录（ephemeral：本页 memory 会话）。
 * @returns {{ ok: true, ephemeral?: boolean } | { ok: false, reason: 'needsConsent'|'busy' }}
 */
export function requireStorageForLogin() {
  const probe = probeLocalStorage();
  if (!hasEssentialStorageConsent()) {
    return { ok: false, reason: 'needsConsent', ephemeral: !probe.ok };
  }
  return { ok: true, ephemeral: !probe.ok };
}

/** Privy SDK storage Cookie 键前缀（与 vantag-* 平台会话隔离） */
const PRIVY_COOKIE_PREFIX = 'ds-privy:';

/** Privy js-sdk-core 写入的 client analytics id（请求头 privy-ca-id，必须是 UUID） */
const PRIVY_CAID_STORAGE_KEY = 'privy:caid';

/**
 * Privy Wallet RPC 等接口用 z.string().uuid() 校验 privy-ca-id；
 * 空串 / "undefined" / clientId 等非 UUID 会返回 `[Input error] ``: Invalid uuid`。
 */
// 与当前 Privy API（Zod v3 string().uuid()）一致，避免收紧成版本/variant 校验引入兼容回归。
const PRIVY_CAID_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function privyCookieName(key) {
  return `${PRIVY_COOKIE_PREFIX}${String(key)}`;
}

function isPrivyCaidKey(key) {
  return String(key) === PRIVY_CAID_STORAGE_KEY;
}

/** @param {unknown} value */
function isValidPrivyCaid(value) {
  const s = String(value ?? '').trim();
  return Boolean(s) && PRIVY_CAID_UUID_RE.test(s);
}

/**
 * Privy `@privy-io/js-sdk-core` Storage：
 * 与平台登录对齐 — memory → localStorage → Cookie 多路降级（不抛 STORAGE_BLOCKED）。
 * 对 `privy:caid` 强制 UUID：非法值一律清除，避免 Wallet RPC 带坏 privy-ca-id。
 * @returns {{ get: Function, put: Function, del: Function, getKeys: Function }}
 */
export function createPrivyBrowserStorage() {
  // 延迟引用，避免 client-storage ↔ auth-cookie 循环时的初始化顺序问题
  const cookieApi = () => import('@/utils/auth-cookie');

  const del = async (key) => {
    const k = String(key);
    privyMemory.delete(k);
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(k);
      }
    } catch {
      /* ignore */
    }
    try {
      const { removeAuthCookie } = await cookieApi();
      removeAuthCookie(privyCookieName(k));
    } catch {
      /* ignore */
    }
  };

  /** @param {string} k @param {unknown} raw */
  const acceptStoredValue = async (k, raw) => {
    if (raw == null) return null;
    const v = String(raw);
    if (isPrivyCaidKey(k) && !isValidPrivyCaid(v)) {
      console.warn('[privy] event=privy_caid_invalid_discarded source=storage');
      await del(k);
      return null;
    }
    privyMemory.set(k, v);
    return v;
  };

  return {
    async get(key) {
      const k = String(key);
      if (privyMemory.has(k)) {
        const mem = await acceptStoredValue(k, privyMemory.get(k));
        if (mem != null) return mem;
      }
      try {
        if (typeof localStorage !== 'undefined') {
          const fromLs = await acceptStoredValue(k, localStorage.getItem(k));
          if (fromLs != null) return fromLs;
        }
      } catch {
        /* use cookie / memory */
      }
      try {
        const { readAuthCookie } = await cookieApi();
        const storedCookie = readAuthCookie(privyCookieName(k));
        if (storedCookie === '' && isPrivyCaidKey(k)) {
          await del(k);
        }
        if (storedCookie != null && storedCookie !== '') {
          const fromCookie = await acceptStoredValue(k, storedCookie);
          if (fromCookie == null) return null;
          try {
            if (typeof localStorage !== 'undefined') {
              localStorage.setItem(k, fromCookie);
            }
          } catch {
            /* memory + cookie 已有 */
          }
          return fromCookie;
        }
      } catch {
        /* ignore */
      }
      return null;
    },
    async put(key, val) {
      const k = String(key);
      const v = String(val);
      if (isPrivyCaidKey(k) && !isValidPrivyCaid(v)) {
        console.warn('[privy] event=privy_caid_invalid_discarded source=write');
        await del(k);
        return;
      }
      privyMemory.set(k, v);
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(k, v);
        }
      } catch {
        /* Cookie 兜底 */
      }
      try {
        const { writeAuthCookie } = await cookieApi();
        writeAuthCookie(privyCookieName(k), v, { maxAgeSec: 60 * 60 * 24 * 7 });
      } catch {
        /* memory-only：本页 Privy 会话仍可用 */
      }
    },
    async del(key) {
      await del(key);
    },
    async getKeys() {
      const keys = new Set(privyMemory.keys());
      try {
        if (typeof localStorage !== 'undefined') {
          for (const k of Object.keys(localStorage)) keys.add(k);
        }
      } catch {
        /* memory-only */
      }
      return [...keys];
    },
  };
}
